import React, { useState, useEffect } from 'react';
import { 
  Database, RefreshCw, CheckCircle, AlertTriangle, 
  ArrowRight, Users, Info, PlayCircle, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MigrazioneVolontari() {
  const [status, setStatus] = useState(null);
  const [anteprima, setAnteprima] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrando, setMigrando] = useState(false);
  const [risultato, setRisultato] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [eliminaPersone, setEliminaPersone] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [statusRes, anteprimaRes] = await Promise.all([
        fetch(`${API_URL}/api/migrazione/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/migrazione/anteprima?limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statusRes.ok) {
        setStatus(await statusRes.json());
      }
      if (anteprimaRes.ok) {
        const data = await anteprimaRes.json();
        setAnteprima(data.volontari_anteprima || []);
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleMigrazione = async () => {
    setShowConfirmDialog(false);
    setMigrando(true);
    setRisultato(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/migrazione/esegui`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          forza_sovrascrittura: false,
          crea_ruolo_volontario: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRisultato(data);
        toast.success(data.messaggio);
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante la migrazione');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setMigrando(false);
    }
  };

  const handleRollback = async () => {
    setShowRollbackDialog(false);
    setMigrando(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/migrazione/rollback?elimina_persone=${eliminaPersone}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.messaggio);
        setRisultato(null);
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante il rollback');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setMigrando(false);
      setEliminaPersone(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="migrazione-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Database className="h-6 w-6" />
          Migrazione Volontari
        </h1>
        <p className="text-gray-500 mt-1">
          Migra i volontari dalla struttura legacy alla nuova architettura "Soggetto Unico"
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">Cos'è l'architettura "Soggetto Unico"?</AlertTitle>
        <AlertDescription className="text-blue-700">
          <p className="mt-1">
            La nuova architettura permette di gestire una <strong>singola persona</strong> con <strong>ruoli multipli</strong>. 
            Una persona può essere contemporaneamente Volontario, Socio, o entrambi, condividendo i dati anagrafici.
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="bg-amber-100 px-2 py-1 rounded">db.volontari</span>
            <ArrowRight className="h-4 w-4" />
            <span className="bg-green-100 px-2 py-1 rounded">db.persone</span>
            <span>+</span>
            <span className="bg-purple-100 px-2 py-1 rounded">db.ruoli_persona</span>
          </div>
        </AlertDescription>
      </Alert>

      {/* Status Card */}
      {status && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {status.migrazione_necessaria ? (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              Stato Migrazione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-gray-900">{status.totale_volontari_legacy}</div>
                <div className="text-sm text-gray-500">Volontari Legacy</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{status.persone_create}</div>
                <div className="text-sm text-gray-500">Persone Create</div>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <div className="text-3xl font-bold text-amber-600">{status.da_migrare}</div>
                <div className="text-sm text-gray-500">Da Migrare</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">{status.ruoli_volontario_creati}</div>
                <div className="text-sm text-gray-500">Ruoli Volontario</div>
              </div>
            </div>

            {status.migrazione_necessaria ? (
              <Alert className="mt-4 bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  Ci sono <strong>{status.da_migrare}</strong> volontari da migrare. 
                  Clicca "Avvia Migrazione" per trasferirli alla nuova architettura.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="mt-4 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Tutti i volontari sono già stati migrati alla nuova architettura!
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Anteprima */}
      {anteprima.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Anteprima Volontari
            </CardTitle>
            <CardDescription>
              Primi 5 volontari che verranno elaborati dalla migrazione
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Nome</th>
                    <th className="text-left py-2 px-3">Codice Fiscale</th>
                    <th className="text-left py-2 px-3">Stato</th>
                    <th className="text-left py-2 px-3">Tipologia</th>
                    <th className="text-left py-2 px-3">Persona Esistente</th>
                  </tr>
                </thead>
                <tbody>
                  {anteprima.map((vol) => (
                    <tr key={vol.volontario_id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium">{vol.nome} {vol.cognome}</td>
                      <td className="py-2 px-3 font-mono text-xs">{vol.codice_fiscale || 'N/D'}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          vol.stato === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {vol.stato}
                        </span>
                      </td>
                      <td className="py-2 px-3">{vol.tipologia?.replace('_', ' ') || '-'}</td>
                      <td className="py-2 px-3">
                        {vol.persona_esistente ? (
                          <span className="text-blue-600 text-xs">Già in db.persone</span>
                        ) : (
                          <span className="text-gray-400 text-xs">Da creare</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risultato Migrazione */}
      {risultato && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              Risultato Migrazione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-green-700 mb-4">{risultato.messaggio}</p>
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white p-3 rounded">
                <div className="font-bold text-lg">{risultato.statistiche?.totale_volontari || 0}</div>
                <div className="text-gray-500">Totale Elaborati</div>
              </div>
              <div className="bg-white p-3 rounded">
                <div className="font-bold text-lg text-green-600">{risultato.statistiche?.migrati_successo || 0}</div>
                <div className="text-gray-500">Migrati con Successo</div>
              </div>
              <div className="bg-white p-3 rounded">
                <div className="font-bold text-lg text-blue-600">{risultato.statistiche?.gia_migrati || 0}</div>
                <div className="text-gray-500">Già Esistenti</div>
              </div>
              <div className="bg-white p-3 rounded">
                <div className="font-bold text-lg text-red-600">{risultato.statistiche?.errori || 0}</div>
                <div className="text-gray-500">Errori</div>
              </div>
            </div>
            {risultato.statistiche?.dettagli_errori?.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 rounded text-sm text-red-700">
                <strong>Errori:</strong>
                <ul className="mt-1 list-disc list-inside">
                  {risultato.statistiche.dettagli_errori.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button 
          onClick={() => setShowConfirmDialog(true)}
          disabled={migrando || !status?.migrazione_necessaria}
          className="bg-green-600 hover:bg-green-700"
        >
          {migrando ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Migrazione in corso...
            </>
          ) : (
            <>
              <PlayCircle className="h-4 w-4 mr-2" />
              Avvia Migrazione
            </>
          )}
        </Button>
        
        <Button 
          variant="outline"
          onClick={fetchStatus}
          disabled={migrando}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna Stato
        </Button>

        {status?.ruoli_volontario_creati > 0 && (
          <Button 
            variant="outline"
            onClick={() => setShowRollbackDialog(true)}
            disabled={migrando}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Rollback
          </Button>
        )}
      </div>

      {/* Note */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>Note importanti:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>La migrazione <strong>non elimina</strong> i dati originali nella collezione <code>volontari</code></li>
              <li>I volontari con lo stesso codice fiscale di una persona esistente verranno <strong>collegati</strong> a quella persona</li>
              <li>I dati specifici del volontario (tipologia, date, ecc.) vengono salvati nel campo <code>dati_specifici</code> del ruolo</li>
              <li>Puoi eseguire il rollback in qualsiasi momento per annullare la migrazione</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Migrazione</DialogTitle>
            <DialogDescription>
              Stai per migrare <strong>{status?.da_migrare || 0}</strong> volontari alla nuova architettura "Soggetto Unico".
              <br /><br />
              Questa operazione creerà nuovi record nelle collezioni <code>persone</code> e <code>ruoli_persona</code>.
              I dati originali in <code>volontari</code> non verranno modificati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleMigrazione} className="bg-green-600 hover:bg-green-700">
              Conferma Migrazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Dialog */}
      <Dialog open={showRollbackDialog} onOpenChange={setShowRollbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Conferma Rollback</DialogTitle>
            <DialogDescription>
              Questa operazione eliminerà i ruoli "volontario" creati dalla migrazione.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="elimina-persone" 
                checked={eliminaPersone}
                onCheckedChange={setEliminaPersone}
              />
              <label htmlFor="elimina-persone" className="text-sm">
                Elimina anche le persone create dalla migrazione
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Attenzione: Se le persone sono state collegate ad altri ruoli (es. Socio), 
              non selezionare questa opzione.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRollbackDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleRollback} variant="destructive">
              Esegui Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
